import { useCallback, useMemo, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { Grid, Tabs, Typography } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AssetCommandForm } from './AssetCommandForm/AssetCommandForm';
import { DiagnosticsHistoryPanel } from './DiagnosticsHistoryPanel/DiagnosticsHistoryPanel';
import { OcppCommandConsole } from './OcppCommandConsole/OcppCommandConsole';
import { commandIconFor } from './commandIcons';
import { useAssetDetailCommandExecution } from './hooks/useAssetDetailCommandExecution';
import { useOcppCommandConsole } from './hooks/useOcppCommandConsole';
import type { AssetCommandUiId, AssetOcppCommandCategory } from './types';
import {
  OCPP_COMMAND_CATEGORIES,
  commandsForCategory,
  findCommandDefinition,
} from './utils';
import { CONSOLE_DESKTOP_HEIGHT, CONSOLE_MOBILE_HEIGHT } from './styles';
import styles from './AssetDetailCommandsTab.module.css';

const { useBreakpoint } = Grid;

type Props = {
  organizationId: string;
  assetId: string;
};

export function AssetDetailCommandsTab({ organizationId, assetId }: Props) {
  const { t } = useTranslation('assets');
  const screens = useBreakpoint();
  const isCompact = !screens.lg;
  const queryClient = useQueryClient();
  const consoleState = useOcppCommandConsole();
  const [category, setCategory] = useState<AssetOcppCommandCategory>('basic');
  const [selectedCommandId, setSelectedCommandId] =
    useState<AssetCommandUiId>('Reset');
  const [heartbeatInterval, setHeartbeatInterval] = useState<string | null>(
    null,
  );
  const [diagnosticsHistoryRevision, setDiagnosticsHistoryRevision] =
    useState(0);

  const onDiagnosticsRequested = useCallback(() => {
    setDiagnosticsHistoryRevision((revision) => revision + 1);
    void queryClient.invalidateQueries({
      queryKey: ['asset-ocpp-diagnostics', assetId],
    });
  }, [assetId, queryClient]);

  const { execute, isPending, activeAction } = useAssetDetailCommandExecution(
    organizationId,
    assetId,
    {
      onConsoleEntries: consoleState.prepend,
      onHeartbeatInterval: setHeartbeatInterval,
      onDiagnosticsRequested,
    },
  );

  const categoryCommands = useMemo(
    () => commandsForCategory(category),
    [category],
  );

  const selectedCommand =
    findCommandDefinition(selectedCommandId) ?? categoryCommands[0];

  const categoryTabItems = useMemo(
    () =>
      OCPP_COMMAND_CATEGORIES.map((item) => ({
        key: item.id,
        label: t(item.labelKey),
      })),
    [t],
  );

  const onCategoryChange = (nextCategory: string) => {
    const categoryId = nextCategory as AssetOcppCommandCategory;
    setCategory(categoryId);
    const first = commandsForCategory(categoryId)[0];
    if (first) {
      setSelectedCommandId(first.id);
    }
  };

  return (
    <div className={styles.workspace}>
      <header className={styles.pageHeader}>
        <Typography.Title level={2} className={styles.pageTitle}>
          {t('detail.commands.panelTitle')}
        </Typography.Title>
        <p className={styles.pageDescription}>
          {t('detail.commands.panelDescription')}
        </p>
      </header>

      <div className={styles.grid}>
        <div className={styles.column}>
          <section
            className={styles.panel}
            aria-label={t('detail.commands.commandListLabel')}
          >
            <Tabs
              className={styles.categoryTabs}
              activeKey={category}
              onChange={onCategoryChange}
              items={categoryTabItems}
              styles={{ content: { display: 'none' } }}
            />

            <Typography.Text className={styles.sectionLabel}>
              {t('detail.commands.commandListLabel')}
            </Typography.Text>

            <div
              className={styles.commandList}
              role="listbox"
              aria-label={t('detail.commands.commandListLabel')}
            >
              {categoryCommands.map((command) => {
                const selected = selectedCommand?.id === command.id;
                return (
                  <button
                    key={command.id}
                    type="button"
                    role="option"
                    disabled={isPending}
                    aria-selected={selected}
                    className={[
                      styles.commandOption,
                      selected ? styles.commandOptionSelected : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => setSelectedCommandId(command.id)}
                  >
                    <span className={styles.commandOptionIcon}>
                      {commandIconFor(command.id)}
                    </span>
                    <span className={styles.commandOptionLabel}>
                      {t(command.titleKey)}
                    </span>
                    {selected ? (
                      <span className={styles.commandOptionCheck} aria-hidden>
                        <CheckOutlined />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </section>

          {selectedCommand ? (
            <section
              className={styles.panel}
              aria-label={t('detail.commands.selectedCommandLabel')}
            >
              <Typography.Text className={styles.sectionLabel}>
                {t('detail.commands.selectedCommandLabel')}
              </Typography.Text>
              <AssetCommandForm
                key={selectedCommand.id}
                command={selectedCommand}
                execute={execute}
                commandsLocked={isPending}
                isActive={
                  activeAction === selectedCommand.action ||
                  (selectedCommand.id === 'UpdateHeartbeat' &&
                    activeAction === 'ChangeConfiguration')
                }
                heartbeatInterval={heartbeatInterval}
                onHeartbeatIntervalChange={setHeartbeatInterval}
              />
            </section>
          ) : null}
        </div>

        <div className={styles.column}>
          <OcppCommandConsole
            entries={consoleState.entries}
            onClear={consoleState.clear}
            height={isCompact ? CONSOLE_MOBILE_HEIGHT : CONSOLE_DESKTOP_HEIGHT}
          />
          <DiagnosticsHistoryPanel
            key={diagnosticsHistoryRevision}
            organizationId={organizationId}
            assetId={assetId}
          />
        </div>
      </div>
    </div>
  );
}
